import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger, Dialog, DialogContent
} from '@duck/ui'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { WaitingList } from '@components/WaitingList/WaitingList'
import { ExternalURLs } from '@/types/externalURLs'
import { useTar } from '@/modules'

export const FaqPage = () => {
    const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)
    const { t } = useTar()

    return (
      <div className="flex flex-1 w-full justify-center">
        <div className="flex-1 max-w-6xl space-y-12 mt-4 md:mt-8">
          {/* Header Section */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-4xl font-bold text-foreground">
              {t('faq.title')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('faq.subtitle')}
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="bg-card rounded-lg shadow-sm border border-border p-8">
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>{t('faq.whatIsXpLab')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>
                      <strong>XP-lab</strong> {t('faq.whatIsXpLabP1Suffix')}
                    </p>
                    <p>{t('faq.whatIsXpLabP2')}</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>{t('faq.whoIsXpLabFor')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>{t('faq.whoIsXpLabForIntro')}</p>
                    <ul className="list-none space-y-2 ms-4">
                      <li>🎓 {t('faq.whoIsXpLabForItem1')}</li>
                      <li>🎓 {t('faq.whoIsXpLabForItem2')}</li>
                      <li>🎓 {t('faq.whoIsXpLabForItem3')}</li>
                      <li>🎓 {t('faq.whoIsXpLabForItem4')}</li>
                      <li>🎓 {t('faq.whoIsXpLabForItem5')}</li>
                    </ul>
                    <p>{t('faq.whoIsXpLabForOutro')}</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>{t('faq.whatDoIGet')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>{t('faq.whatDoIGetIntro')}</p>
                    <ul className="list-disc space-y-2 ms-6">
                      <li>{t('faq.whatDoIGetItem1')}</li>
                      <li>{t('faq.whatDoIGetItem2')}</li>
                      <li>{t('faq.whatDoIGetItem3')}</li>
                      <li>{t('faq.whatDoIGetItem4')}</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>{t('faq.whatKindOfTasks')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>{t('faq.whatKindOfTasksIntro')}</p>
                    <p>{t('faq.whatKindOfTasksExamples')}</p>
                    <ul className="list-disc space-y-2 ms-6">
                      <li>{t('faq.whatKindOfTasksItem1')}</li>
                      <li>{t('faq.whatKindOfTasksItem2')}</li>
                      <li>{t('faq.whatKindOfTasksItem3')}</li>
                      <li>{t('faq.whatKindOfTasksItem4')}</li>
                    </ul>
                    <p>
                      {t('faq.whatKindOfTasksOutroPrefix')}{' '}
                      <strong>{t('faq.whatKindOfTasksOutroBold')}</strong>.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger>{t('faq.doIGetFeedback')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>{t('faq.doIGetFeedbackIntro')}</p>
                    <ul className="list-disc space-y-2 ms-6">
                      <li>{t('faq.doIGetFeedbackItem1')}</li>
                      <li>{t('faq.doIGetFeedbackItem2')}</li>
                      <li>
                        {t('faq.doIGetFeedbackItem3Prefix')}{' '}
                        <strong>{t('faq.doIGetFeedbackItem3Bold')}</strong>
                        {t('faq.doIGetFeedbackItem3Suffix')}
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10">
                <AccordionTrigger>{t('faq.willThisHelpJob')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>
                      {t('faq.willThisHelpJobIntroPrefix')}{' '}
                      <strong>{t('faq.willThisHelpJobIntroBold')}</strong>:
                    </p>
                    <ul className="list-disc space-y-2 ms-6">
                      <li>{t('faq.willThisHelpJobItem1')}</li>
                      <li>{t('faq.willThisHelpJobItem2')}</li>
                      <li>{t('faq.willThisHelpJobItem3')}</li>
                      <li>{t('faq.willThisHelpJobItem4')}</li>
                    </ul>
                    <p>
                      {t('faq.willThisHelpJobOutroPrefix')}{' '}
                      <strong>{t('faq.willThisHelpJobOutroBold')}</strong>{' '}
                      {t('faq.willThisHelpJobOutroSuffix')}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-11">
                <AccordionTrigger>{t('faq.whatSkills')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>{t('faq.whatSkillsIntro')}</p>
                    <ul className="list-disc space-y-2 ms-6">
                      <li>{t('faq.whatSkillsItem1')}</li>
                      <li>{t('faq.whatSkillsItem2')}</li>
                    </ul>
                    <p>
                      {t('faq.whatSkillsOutroPrefix')}{' '}
                      <strong>{t('faq.whatSkillsOutroBold')}</strong>.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-12">
                <AccordionTrigger>{t('faq.canIChatGPT')}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <p>{t('faq.canIChatGPTP1')}</p>
                    <p>{t('faq.canIChatGPTP2')}</p>
                    <p>{t('faq.canIChatGPTP3')}</p>
                    <ul className="list-disc space-y-2 ms-6">
                      <li>{t('faq.canIChatGPTItem1')}</li>
                      <li>{t('faq.canIChatGPTItem2')}</li>
                      <li>{t('faq.canIChatGPTItem3')}</li>
                      <li>{t('faq.canIChatGPTItem4')}</li>
                    </ul>
                    <p>{t('faq.canIChatGPTP4')}</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-13">
                <AccordionTrigger>{t('faq.foundABug')}</AccordionTrigger>
                <AccordionContent>
                  <p>
                    {t('faq.foundABugAnswerPrefix')}
                    <a
                      href={ExternalURLs.DiscordInviteIntoBugReportChannel}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ms-1 text-sm text-muted-foreground underline hover:text-primary transition-colors"
                    >
                      {t('faq.reportABug')}
                    </a>
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-14">
                <AccordionTrigger>{t('faq.isMyWorkPrivate')}</AccordionTrigger>
                <AccordionContent>
                  <p>
                    {t('faq.isMyWorkPrivateAnswerPrefix')}{' '}
                    <Link
                      to="/privacy-policy"
                      className="text-accent hover:text-foreground transition-colors underline underline-offset-4"
                    >
                      {t('common.privacyPolicy')}
                    </Link>
                    .
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-16">
                <AccordionTrigger>{t('faq.whenIsLaunching')}</AccordionTrigger>
                <AccordionContent>
                  <p>
                    {t('faq.whenIsLaunchingPrefix')}{' '}
                    <button
                      type="button"
                      onClick={() => setIsWaitlistOpen(true)}
                      className="text-accent hover:text-foreground transition-colors underline underline-offset-4"
                    >
                      {t('faq.waitlist')}
                    </button>{' '}
                    {t('faq.whenIsLaunchingOrContact')}
                    <a
                      href="mailto:tal@xp-lab.com"
                      className="text-accent hover:text-foreground transition-colors ms-1"
                    >
                      tal@xp-lab.com
                    </a>
                    .
                  </p>
                  <Dialog
                    open={isWaitlistOpen}
                    onOpenChange={setIsWaitlistOpen}
                  >
                    <DialogContent className="sm:max-w-lg">
                      <WaitingList />
                    </DialogContent>
                  </Dialog>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    )
}
